import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ReferralProgram.css";

export default function ReferralProgram() {
  const navigate = useNavigate();
  const [activeAccordion, setActiveAccordion] = useState(1);

  const handleSignUp = () => {
    navigate("/auth/signup-referral");
  };

  const handleApplyNow = () => {
    navigate("/auth/signup-referral");
  };

  // Hero section data
  const heroData = {
    title: "HIGH SCHOOL FISHING SPONSORSHIP PROGRAM",
    subtitle:
      "A referral-based fundraising partnership built for youth anglers",
    ctaText: "SIGN UP",
    backgroundImage: "/assets/backend/images/refferal-images/hero-image.webp",
  };

  // Why we're giving back section data
  const whyWeGiveBackData = {
    title: "WHY WE'RE GIVING BACK",
    paragraphs: [
      "BBR Boat Rentals is a 5 Star rated Texas based, boat rental booking platform offering tournament ready bass boat rentals.",
      "We were inspired by the needs of high school teams looking for affordable boats for competition. Today, high school anglers make up one of our largest client groups.",
      "To support the sport that started it all, we've launched a referral-based sponsorship program that helps teams raise money, support families, and expand access to bass boats across the USA.",
    ],
    image: {
      src: "/assets/backend/images/refferal-images/bbr-sponsored-team.jpg",
      alt: "BBR sponsored high school bass fishing team",
    },
  };

  // How sponsorship works section data
  const sponsorshipWorksData = {
    title: "HOW THE SPONSORSHIP PROGRAM WORKS",
    subtitle:
      "No cost. No selling. No events to plan. Teams earn money simply by sharing a referral code.",
    steps: [
      {
        id: 1,
        title: "GET YOUR CODE",
        content:
          "Each team gets a unique referral code from BBR that tracks and rewards every qualifying rental or boat post listing.",
        image: "/assets/backend/images/refferal-images/Sponsorship-one.jpg",
      },
      {
        id: 2,
        title: "SPREAD THE WORD",
        content:
          "Team leaders share the code with members through email, text, or group meetings—BBR provides templates to help.",
        image: "/assets/backend/images/refferal-images/Sponsorship-two.jpg",
      },
      {
        id: 3,
        title: "START EARNING",
        content:
          "Members share the code with supporters. When someone books a rental or lists a boat that gets rented, your team earns cash.",
        image: "/assets/backend/images/refferal-images/Sponsorship-three.jpg",
      },
    ],
  };

  // Earn money section data
  const earnMoneyData = {
    title: "EARN MONEY WITH EVERY REFERRAL",
    backgroundImage: "/assets/backend/images/refferal-images/earn-money.webp",
    rewards: [
      {
        id: 1,
        amount: "$25",
        description:
          "for every new boat listing (paid after its first completed rental) **",
      },
      {
        id: 2,
        amount: "$10",
        description: "for every completed rental booked with your code *",
      },
    ],
    footnotes: [
      "**The team with the most referral rewards by December 31 receives a matching payout from BBR. This award is paid annually.",
      "*The rental amount must be $400 or more for a referral payout (current average is $600)",
    ],
  };

  // What we provide section data
  const whatWeProvideData = {
    title: "WHAT WE PROVIDE",
    subtitle: "To make your team's success easier, BBR supplies:",
    services: [
      {
        id: 1,
        title: "A custom referral code for your team",
        subtitle: "code for your team",
        image: "/assets/backend/images/refferal-images/customerRef.png",
        alt: "A custom referral",
      },
      {
        id: 2,
        title: "Monthly, quarterly, or annual payouts",
        image: "/assets/backend/images/refferal-images/payouts.png",
        alt: "Monthly, quarterly, or annual payouts",
      },
      {
        id: 3,
        title: "Ready-to-use marketing materials",
        image: "/assets/backend/images/refferal-images/marketing.png",
        alt: "Ready-to-use marketing",
      },
      {
        id: 4,
        title: "Paid ad support on social media",
        image: "/assets/backend/images/refferal-images/social.png",
        alt: "Paid ad support on social media",
      },
    ],
  };

  // Accordion data array
  const accordionData = [
    {
      id: 1,
      title: "1. SETUP",
      content:
        "Once your team signs up, BBR assigns a referral code linked to your club. No setup fees, no contracts.",
    },
    {
      id: 2,
      title: "2. BROADCAST",
      content:
        "Your advisor, coach, or team captain shares the code with members. We provide graphics and messaging to make it easy.",
    },
    {
      id: 3,
      title: "3. ACTIVATE",
      content:
        "Members share the code with their network. Boat renters and owners use it, and your team earns money.",
    },
    {
      id: 4,
      title: "4. TRACK & EARN",
      content:
        "BBR manages referral counts and sends reports regularly. Payouts are issued on your team's chosen schedule.",
    },
  ];

  // Why teams love section data
  const whyTeamsLoveData = {
    title: "WHY TEAMS LOVE THE \n BBR REFERRAL PROGRAM",
    backgroundImage: "/assets/backend/images/refferal-images/team.webp",
    benefits: [
      "New fundraising option without traditional fundraisers",
      "More boats mean more tournament access for student anglers",
      "No fees, no inventory, and no risk",
    ],
  };

  // Tournament ready boats section data
  const tournamentBoatsData = {
    title: "EXPLORE OUR TOURNAMENT-READY BASS BOATS",
    subtitle: "Here are a few examples available across Texas:",
    boats: [
      {
        id: 1,
        name: "Phoenix 921 Elite",
        image: "/assets/backend/images/refferal-images/phoenix.jpg",
        alt: "Phoenix 921 Elite",
      },
      {
        id: 2,
        name: "Bass Cat",
        image: "/assets/backend/images/refferal-images/BassCat.jpg",
        alt: "Bass Cat",
      },
      {
        id: 3,
        name: "Skeeter",
        image: "/assets/backend/images/refferal-images/Skeeter.jpg",
        alt: "Skeeter",
      },
    ],
    footerText: "New listings are added every week.",
  };

  // Join program section data
  const joinProgramData = {
    title: "JOIN THE PROGRAM TODAY",
    description:
      "Partner with BBR Boat Rentals to support youth fishing, increase boat access, and earn funding for your team. No extra work required.",
    ctaText: "CREATE REFERRAL CODE NOW",
  };

  const handleAccordionClick = (accordionId) => {
    setActiveAccordion(activeAccordion === accordionId ? null : accordionId);
  };

  return (
    <div className="bg-bbr-white">
      {/* Logo positioned above hero background */}
      <div className="position-relative">
        {/* Hero Section with Background */}
        <section
          className="hero-section d-flex align-items-center justify-content-center text-center position-relative section-padding"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('/assets/backend/images/refferal-images/hero-image.webp')`,
          }}
        >
          <div className="container hero-container flex-column section-container-large z-index-10">
            <h1 className="hero-title text-white mb-5 text-hero mb-medium">
              {heroData.title}
            </h1>
            <div className="hero-subtitle text-white mb-5">
              <span className="hero-subtitle text-white mb-5">
                {heroData.subtitle}
              </span>
            </div>
            <button
              onClick={handleSignUp}
              className="btn btn-outline-light text-uppercase cta-button"
            >
              {heroData.ctaText}
            </button>
          </div>
        </section>
      </div>

      {/* Why We're Giving Back */}
      <section className="bg-bbr-white section-padding">
        <div className="section-container">
          <div className="grid-2">
            <div className="flex-1 min-width-300">
              <h2 className="section-title bbr-yellow mb-5 text-section mb-medium">
                {whyWeGiveBackData.title}
              </h2>
              <div className="body-text bbr-black text-large">
                {whyWeGiveBackData.paragraphs.map((paragraph, index) => (
                  <p
                    key={index}
                    className={`body-text-medium ${
                      index === whyWeGiveBackData.paragraphs.length - 1
                        ? "mb-0"
                        : "mb-small"
                    }`}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
            <div className="flex-center flex-1 min-width-300">
              <div className="hero-image">
                <img
                  src={whyWeGiveBackData.image.src}
                  alt={whyWeGiveBackData.image.alt}
                  className="rounded-t-full grayscale boat-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How the Sponsorship Program Works */}
      <section className="bg-bbr-black text-white section-padding">
        <div className="section-container">
          <div className="text-center mb-80">
            <h2 className="bbr-yellow mb-4 sponsorship-title">
              {sponsorshipWorksData.title}
            </h2>
            <div className="body-text-large text-white">
              <span>{sponsorshipWorksData.subtitle}</span>
            </div>
          </div>

          <div className="grid-3">
            {sponsorshipWorksData.steps.map((step) => (
              <div key={step.id} className="step-card">
                <div className="step-image">
                  <img
                    src={step.image}
                    alt="BBR sponsored high school bass fishing team"
                    className="rounded-t-full grayscale boat-image"
                  />
                </div>
                <h3 className="sponsored-title text-white mb-4 mb-small">
                  {step.title}
                </h3>
                <p className="body-text text-white body-text-medium text-small">
                  {step.content}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earn Money Section with Background */}
      <section
        className="earn-money-section position-relative section-padding"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('/assets/backend/images/refferal-images/earn-money.webp')`,
        }}
      >
        <div className="section-container-large text-center z-index-10">
          <h2 className="earn-money-title mb-5 text-white mb-large">
            {earnMoneyData.title}
          </h2>

          <div className="gap-large mb-large flex-wrap flex-center">
            {earnMoneyData.rewards.map((reward) => (
              <div key={reward.id} className="reward-card">
                <div className="bbr-yellow text-reward">{reward.amount}</div>
                <p className="body-text-medium text-white text-medium">
                  {reward.description}
                </p>
              </div>
            ))}
          </div>

          <div className="body-text text-white text-center text-small max-width-800 mx-auto">
            {earnMoneyData.footnotes.map((footnote, index) => (
              <p
                key={index}
                className={`body-text-medium ${
                  index === earnMoneyData.footnotes.length - 1
                    ? "mb-0"
                    : "mb-small"
                }`}
              >
                {footnote}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* What We Provide */}
      <section className="bg-bbr-white section-padding">
        <div className="section-container text-center">
          <h2 className="section-title bbr-yellow mb-4 text-section mb-small">
            {whatWeProvideData.title}
          </h2>
          <p className="body-text bbr-black mb-5 mb-large">
            {whatWeProvideData.subtitle}
          </p>

          <div className="grid-3 service-para">
            {whatWeProvideData.services.map((service) => (
              <div key={service.id} className="service-card">
                <div className="service-icon">
                  <img
                    src={service.image}
                    alt={service.alt}
                    className="service-icon-img"
                  />
                </div>
                <h3 className="body-text-semibold bbr-black mb-2 mb-small">
                  {service.title}
                </h3>
               
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Getting Started Section with Accordions */}
      <section className="bg-bbr-black text-white section-padding">
        <div className="section-container">
          <div className="grid-2 align-items-flex-start">
            <div className="flex-center flex-1 min-width-300 max-width-600 padding-top-clamp">
              <h2 className="section-title bbr-yellow mb-5 text-hero mb-large letter-spacing text-center">
                GETTING
                <br />
                STARTED
                <br />
                IS EASY
              </h2>
            </div>
            <div className="flex-1 min-width-320 max-width-600">
              <div className="flex-wrap flex-direction-column gap-clamp">
                {accordionData.map((accordion) => {
                  const isActive = activeAccordion === accordion.id;
                  return (
                    <div
                      key={accordion.id}
                      className="accordion-item border-radius-12 box-shadow"
                      style={{
                        border: "none",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        className={`accordion-header padding-clamp cursor-pointer ${
                          isActive ? "active" : ""
                        }`}
                        onClick={() => handleAccordionClick(accordion.id)}
                      >
                        <h3 className="section-title text-white mb-0 text-medium flex-between">
                          {accordion.title}
                          <span
                            className={`accordion-icon font-size-1-5 font-weight-bold ${
                              isActive ? "rotated" : ""
                            }`}
                          >
                            +
                          </span>
                        </h3>
                      </div>
                      <div
                        className={`accordion-collapse accordion-content ${
                          isActive ? "open" : "closed"
                        }`}
                      >
                        <p className="body-text text-white mb-0 body-text-medium text-small">
                          {accordion.content}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Teams Love Section with Background */}
      <section
        className="teams-love-section position-relative section-padding"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('/assets/backend/images/refferal-images/team.webp')`,
        }}
      >
        <div className="section-container-large text-center z-index-10">
          <h2 className="section-title mb-7 text-white text-section mb-large">
            {whyTeamsLoveData.title.split("\n").map((line, index) => (
              <React.Fragment key={index}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </h2>

          <div
            className="flex-wrap flex-direction-column margin-0"
            style={{
              margin: "0 auto",
              gap: "clamp(5px, 2vw, 5px)",
            }}
          >
            {whyTeamsLoveData.benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex-wrap gap-clamp-small min-height-clamp padding-clamp-small"
                style={{
                  alignItems: "center",
                  textAlign: "left",
                }}
              >
                <div className="checkmark-icon">
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </div>
                <div className="benefits-text">
                  <span className="text-white mb-0 margin-0 flex-1 white-space-wrap word-break-all">
                    {benefit}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tournament Ready Boats */}
      <section className="bg-bbr-black text-white section-padding">
        <div className="section-container text-center">
          <h2 className="bbr-yellow mb-6 tournanment-title mb-medium">
            {tournamentBoatsData.title}
          </h2>
          <div className="grid-3 mb-medium">
            {tournamentBoatsData.boats.map((boat) => (
              <div key={boat.id} className="boat-card">
                <div className="bg-bbr-dark-grey rounded mb-4 overflow-hidden position-relative height-256">
                  <img src={boat.image} alt={boat.alt} className="boat-image" />
                  <div className="hover-overlay d-flex align-items-center justify-content-center">
                    <div className="text-center">
                      <h3 className="section-title text-white mb-3 text-medium mb-small">
                        {boat.name}
                      </h3>
                      <button className="btn bg-bbr-yellow text-dark text-uppercase body-text-medium">
                        View Listing
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="footer-text text-white">
            {tournamentBoatsData.footerText}
          </p>
        </div>
      </section>

      {/* Join Program Section */}
      <section className="bg-bbr-dark-grey text-white section-padding">
        <div className="section-container-large text-center">
          <h2 className="section-title bbr-yellow mb-4 text-section mb-medium">
            {joinProgramData.title}
          </h2>
          <p className="body-text-large join-program-text text-white mb-5 mx-auto text-medium mb-large">
            {joinProgramData.description}
          </p>
          <button
            onClick={handleApplyNow}
            className="btn btn-outline-warning text-uppercase apply-button text-white"
          >
            {joinProgramData.ctaText}
          </button>
        </div>
      </section>
    </div>
  );
}
